import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { PageHeader, SectionCard } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Archive, Trash2, Undo2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Badge, SUBSCRIPTION_LABEL, ACCOUNT_LABEL, fmtAcquisitionSource, fmtAcquisitionText } from "@/pages/admin/AdminCompanies";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import RenewalSettingsCard from "@/components/admin/RenewalSettingsCard";

function Row({ label, children }) {
  return (
    <div className="flex justify-between items-center gap-4 py-2.5 border-b border-slate-100 last:border-0">
      <span className="text-xs uppercase tracking-wider text-slate-500">{label}</span>
      <span className="text-sm text-slate-900 text-right">{children}</span>
    </div>
  );
}

const PERIOD_LABEL = {
  monthly: "Aylık", yearly: "Yıllık", quarterly: "3 Aylık",
  weekly: "Haftalık", one_time: "Tek Seferlik",
};

const BILLING_TYPE_LABEL = {
  individual: "Şahıs",
  company: "Şirket",
};

const COMPANY_KIND_LABEL = {
  sahis_sirketi: "Şahıs Şirketi",
  limited: "Limited Şirket",
  anonim: "Anonim Şirket",
  diger: "Diğer",
};

// Turkish "400 TL" / "4.000 TL" — no decimals, no ₺ glyph.
const fmtFeeTL = (n) => {
  if (n == null || n === "") return "—";
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return `${new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 0 }).format(v)} TL`;
};

// Add 1 month / 1 year to an ISO date, clamping to that month's last day.
// e.g. computeNextPayment("2026-01-31", "monthly") → "2026-02-28".
const computeNextPayment = (last, period) => {
  if (!last || (period !== "monthly" && period !== "yearly")) return "";
  const [y, m, d] = last.split("-").map(Number);
  if (!y || !m || !d) return "";
  let ny = y, nm = m;
  if (period === "monthly") {
    nm = m + 1;
    if (nm > 12) { nm = 1; ny = y + 1; }
  } else {
    ny = y + 1;
  }
  // JS Date(year, month, 0) → last day of the previous month, i.e. month `nm`.
  const lastDay = new Date(ny, nm, 0).getDate();
  const nd = Math.min(d, lastDay);
  return `${ny}-${String(nm).padStart(2, "0")}-${String(nd).padStart(2, "0")}`;
};

// Son login sonrası oluşturulan yeni kayıtların özetini Türkçe okunabilir
// biçimde döndürür.  Tek işlem varsa "Yeni X oluşturuldu"; çoklu ise
// "n Kategori • m Kategori"; hiçbiri yoksa "Son işlem yapılmadı".
function fmtSessionActivity(a) {
  const KATS = [
    ["customer_count",   "Müşteri",    "Yeni Müşteri oluşturuldu"],
    ["vendor_count",     "Tedarikçi",  "Yeni Tedarikçi oluşturuldu"],
    ["job_count",        "İş",         "Yeni İş oluşturuldu"],
    ["collection_count", "Tahsilat",   "Yeni Tahsilat oluşturuldu"],
    ["purchase_count",   "Satın Alma", "Yeni Satın Alma oluşturuldu"],
    ["expense_count",    "Gider",      "Yeni Gider oluşturuldu"],
  ];
  if (!a) return "Son işlem yapılmadı";
  const active = KATS.filter(([k]) => Number(a[k] || 0) > 0);
  if (active.length === 0) return "Son işlem yapılmadı";
  const total = active.reduce((s, [k]) => s + Number(a[k] || 0), 0);
  if (active.length === 1 && total === 1) return active[0][2];
  return active.map(([k, singular]) => `${a[k]} ${singular}`).join(" • ");
}

export default function AdminCompanyDetail() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mgmtAccount, setMgmtAccount] = useState("active");
  const [mgmtSubscription, setMgmtSubscription] = useState("trial");
  const [mgmtPeriod, setMgmtPeriod] = useState("");
  const [mgmtFee, setMgmtFee] = useState("");
  const [mgmtLastPayment, setMgmtLastPayment] = useState("");
  const [mgmtNextPayment, setMgmtNextPayment] = useState("");
  const [saving, setSaving] = useState(false);
  // Lifecycle action state — archive / soft-delete / restore / permanent
  const [lifecycleAction, setLifecycleAction] = useState(null); // 'archive'|'soft-delete'|'restore'
  const [permStep1Open, setPermStep1Open] = useState(false);
  const [permStep2Open, setPermStep2Open] = useState(false);
  const [permTypedName, setPermTypedName] = useState("");
  const [lifecycleBusy, setLifecycleBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/admin/companies/${companyId}`);
        setC(data);
        setMgmtAccount(data.account_status === "active" ? "active" : "passive");
        // Effective subscription may be "trial_expired"; management form only
        // exposes the three writable values (trial / paid / internal_free).
        const sub = data.subscription_status;
        setMgmtSubscription(
          sub === "internal_free" ? "internal_free"
            : sub === "paid" || sub === "active" ? "paid"
            : "trial",
        );
        setMgmtPeriod(
          data.payment_period === "monthly" || data.payment_period === "yearly"
            ? data.payment_period : "",
        );
        setMgmtFee(data.fee == null ? "" : String(data.fee));
        setMgmtLastPayment(data.last_payment_date ? data.last_payment_date.slice(0, 10) : "");
        setMgmtNextPayment(data.next_payment_date ? data.next_payment_date.slice(0, 10) : "");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Firma yüklenemedi");
      } finally { setLoading(false); }
    })();
  }, [companyId]);

  // Auto-derive next payment date whenever the period or last payment date
  // changes.  Field is read-only in the UI; this is the single source of
  // truth on the client (backend recomputes on save regardless).
  useEffect(() => {
    setMgmtNextPayment(computeNextPayment(mgmtLastPayment, mgmtPeriod));
  }, [mgmtLastPayment, mgmtPeriod]);

  const onSave = async () => {
    const feeNum = mgmtFee === "" ? null : Number(mgmtFee);
    if (feeNum != null && (!Number.isFinite(feeNum) || feeNum < 0)) {
      toast.error("Ücret negatif olamaz.");
      return;
    }
    const paymentActive = mgmtSubscription === "paid";
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/companies/${companyId}/management`, {
        account_status: mgmtAccount,
        subscription_status: mgmtSubscription,
        payment_period: paymentActive && mgmtPeriod ? mgmtPeriod : null,
        fee: paymentActive ? feeNum : null,
        last_payment_date: paymentActive && mgmtLastPayment ? mgmtLastPayment : null,
        next_payment_date: paymentActive && mgmtNextPayment ? mgmtNextPayment : null,
      });
      setC(data);
      setMgmtPeriod(
        data.payment_period === "monthly" || data.payment_period === "yearly"
          ? data.payment_period : "",
      );
      setMgmtFee(data.fee == null ? "" : String(data.fee));
      setMgmtLastPayment(data.last_payment_date ? data.last_payment_date.slice(0, 10) : "");
      setMgmtNextPayment(data.next_payment_date ? data.next_payment_date.slice(0, 10) : "");
      toast.success("Firma bilgileri başarıyla güncellendi.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Firma bilgileri güncellenemedi.");
    } finally { setSaving(false); }
  };

  const runLifecycle = async () => {
    if (!lifecycleAction) return;
    setLifecycleBusy(true);
    try {
      const { data } = await api.patch(
        `/admin/companies/${companyId}/${lifecycleAction}`,
        {},
      );
      setC(data);
      // Sync management form to reflect the new state (never active on
      // restore).  Frontend mgmtAccount only supports active/passive; when
      // firma is archived/deleted, the management form is disabled.
      setMgmtAccount(data.account_status === "active" ? "active" : "passive");
      const successMap = {
        "archive": "Firma arşive alındı.",
        "soft-delete": "Firma Silinmişler'e taşındı.",
        "restore":
          data.account_status === "archived"
            ? "Firma Arşiv durumuna geri yüklendi."
            : "Firma Pasif duruma geri yüklendi.",
      };
      toast.success(successMap[lifecycleAction] || "İşlem tamamlandı.");
      setLifecycleAction(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "İşlem başarısız oldu.");
    } finally { setLifecycleBusy(false); }
  };

  const runPermanent = async () => {
    setLifecycleBusy(true);
    try {
      await api.delete(`/admin/companies/${companyId}/permanent`);
      toast.success("Firma kalıcı olarak silindi.");
      setPermStep2Open(false);
      setPermTypedName("");
      navigate("/admin/deleted");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 501) {
        toast.warning(
          err?.response?.data?.detail ||
            "Kalıcı silme henüz uygulanmadı. PayTR audit verilerinin korunması ve cascade delete analizi tamamlandıktan sonra aktifleştirilecektir.",
          { duration: 8000 },
        );
      } else {
        toast.error(err?.response?.data?.detail || "Kalıcı silme başarısız oldu.");
      }
      setPermStep2Open(false);
      setPermTypedName("");
    } finally { setLifecycleBusy(false); }
  };

  if (loading) return <div className="text-sm text-slate-500">Yükleniyor…</div>;
  if (!c) return null;

  const isInternalFree = c.subscription_status === "internal_free";
  const remaining = isInternalFree
    ? "Sınırsız"
    : c.subscription_status === "trial_expired"
      ? "Deneme Süresi Doldu"
      : (c.days_left == null ? "—" : `${c.days_left} gün`);

  const feeText = isInternalFree ? "—" : fmtFeeTL(c.fee);
  const paymentEditable = mgmtSubscription === "paid";
  // Lifecycle state helpers — archived / deleted firmalar için yönetim
  // formu kilitlenir; yaşam döngüsü aksiyonları duruma göre gösterilir.
  const lifeState = c.account_status; // "active"|"passive"|"archived"|"deleted"
  const isLocked = lifeState === "archived" || lifeState === "deleted";
  const canArchive = lifeState === "passive";
  const canSoftDelete = lifeState === "archived";
  const canRestore = lifeState === "archived" || lifeState === "deleted";
  const canPermanent = lifeState === "deleted";

  const lifecycleCopy = {
    archive: {
      title: "Firmayı arşive al",
      body: (
        <>
          <span className="font-medium">{c.name}</span> firması <b>Arşiv</b> durumuna alınacak. Firma verileri korunur; giriş engellenir. 12 ay sonra Silinmişler&apos;e taşınabilir; admin gerekli görürse süreyi beklemeden Silinmişler&apos;e alabilir.
        </>
      ),
      ok: "Arşive Al",
      cls: "bg-amber-600 hover:bg-amber-700",
    },
    "soft-delete": {
      title: "Firmayı Silinmişler'e taşı",
      body: (
        <>
          <span className="font-medium">{c.name}</span> firması <b>Silinmişler</b> bölümüne taşınacak. Kalıcı imha yapılmaz; veriler korunur ve geri yüklenebilir.
        </>
      ),
      ok: "Silinmişler'e Taşı",
      cls: "bg-rose-600 hover:bg-rose-700",
    },
    restore: {
      title: "Firmayı geri yükle",
      body: lifeState === "deleted" ? (
        <>
          <span className="font-medium">{c.name}</span> firması Silinmişler&apos;den çıkarılıp <b>Arşiv</b> durumuna alınacak. Silinme tarihi temizlenir; abonelik otomatik olarak aktifleştirilmez.
        </>
      ) : (
        <>
          <span className="font-medium">{c.name}</span> firması arşivden çıkarılıp <b>Pasif</b> duruma alınacak. Firma verileri korunur; abonelik otomatik olarak aktifleştirilmez.
        </>
      ),
      ok: "Geri Yükle",
      cls: "bg-slate-900 hover:bg-slate-800",
    },
  };

  return (
    <div className="space-y-6" data-testid="admin-company-detail">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          className="text-slate-600 hover:bg-slate-100 rounded-lg"
          onClick={() => navigate("/admin/companies")}
          data-testid="admin-detail-back"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Firmalara Dön
        </Button>
      </div>

      <PageHeader
        title={c.name || "—"}
        subtitle={c.owner?.username ? `@${c.owner.username}` : "Firma detayı (salt okunur)"}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SectionCard title="Firma Bilgileri">
          <div className="p-5" data-testid="admin-detail-company-info">
            <Row label="Firma Adı">{c.name || "—"}</Row>
            <Row label="Yetkili Adı">{c.owner?.full_name || "—"}</Row>
            <Row label="Telefon">{c.owner?.phone || "—"}</Row>
            <Row label="E-posta">{c.owner?.email || "—"}</Row>
            <Row label="Kullanıcı Adı">{c.owner?.username || "—"}</Row>
            <Row label="Kayıt Tarihi">{c.created_at ? fmtDate(c.created_at.slice(0, 10)) : "—"}</Row>
            <Row label="Son Giriş">
              {c.owner?.last_login_at ? fmtDateTime(c.owner.last_login_at) : "—"}
            </Row>
          </div>
        </SectionCard>

        <SectionCard title="Abonelik Bilgileri">
          <div className="p-5" data-testid="admin-detail-subscription-info">
            <Row label="Abonelik Durumu">
              <Badge tone={SUBSCRIPTION_LABEL[c.subscription_status]} />
            </Row>
            <Row label="Hesap Durumu">
              <Badge tone={ACCOUNT_LABEL[c.account_status]} />
            </Row>
            <Row label="Deneme Başlangıç">
              {isInternalFree ? "—" : (c.trial_start_date ? fmtDate(c.trial_start_date.slice(0, 10)) : "—")}
            </Row>
            <Row label="Deneme Bitiş">
              {isInternalFree ? "—" : (c.trial_end_date ? fmtDate(c.trial_end_date.slice(0, 10)) : "—")}
            </Row>
            <Row label="Kalan Deneme Günü">
              <span className={isInternalFree ? "text-indigo-700" : (c.subscription_status === "trial_expired" ? "text-rose-600" : "")}>
                {remaining}
              </span>
            </Row>
            <Row label="Abonelik Başlangıç">
              {c.subscription_start_date ? fmtDate(c.subscription_start_date.slice(0, 10)) : "—"}
            </Row>
            <Row label="Son Ödeme Tarihi">
              {isInternalFree ? "—" : (c.last_payment_date ? fmtDate(c.last_payment_date.slice(0, 10)) : "—")}
            </Row>
            <Row label="Sonraki Ödeme Tarihi">
              {isInternalFree ? "—" : (c.next_payment_date ? fmtDate(c.next_payment_date.slice(0, 10)) : "—")}
            </Row>
            <Row label="Ödeme Dönemi">
              {isInternalFree ? "—" : (PERIOD_LABEL[c.payment_period] || c.payment_period || "—")}
            </Row>
            <Row label="Ücret">
              {isInternalFree ? "—" : feeText}
            </Row>
          </div>
        </SectionCard>

        {/* Kazanım Kaynağı — /register?utm_source=... akışında toplanan
            UTM alanları.  Boş / eksik değerler "—" gösterir.  Bilinen
            kaynaklar (instagram, linkedin, google...) Türkçe kısa
            etiketle formatlanır; kanal ve kampanya ham değeriyle
            baş harfi büyük gösterilir. */}
        <SectionCard title="Kazanım Kaynağı">
          <div className="p-5" data-testid="admin-detail-acquisition-info">
            <Row label="Kaynak">
              <span data-testid="admin-detail-acquisition-source">
                {fmtAcquisitionSource(c.acquisition?.source)}
              </span>
            </Row>
            <Row label="Kanal">
              {fmtAcquisitionText(c.acquisition?.medium)}
            </Row>
            <Row label="Kampanya">
              {fmtAcquisitionText(c.acquisition?.campaign)}
            </Row>
          </div>
        </SectionCard>

        <SectionCard title="Aktivite Özeti">
          <div className="p-5" data-testid="admin-detail-activity">
            <Row label="Son Giriş">
              {c.owner?.last_login_at ? fmtDateTime(c.owner.last_login_at) : "—"}
            </Row>
            <Row label="Son İşlem">
              <span className="font-medium text-slate-900 break-words" data-testid="admin-detail-last-session">
                {fmtSessionActivity(c.activity?.last_session_activity)}
              </span>
            </Row>
            <Row label="Son Aktivite">
              {c.activity?.last_activity_at ? fmtDateTime(c.activity.last_activity_at) : "—"}
            </Row>
            <Row label="Kayıt Tarihi">
              {c.created_at ? fmtDateTime(c.created_at) : "—"}
            </Row>
            <Row label="Müşteri">{c.activity?.customer_count ?? 0}</Row>
            <Row label="Tedarikçi">{c.activity?.vendor_count ?? 0}</Row>
            <Row label="İş">{c.activity?.job_count ?? 0}</Row>
            <Row label="Tahsilat">{c.activity?.collection_count ?? 0}</Row>
            <Row label="Satın Alma">{c.activity?.purchase_count ?? 0}</Row>
            <Row label="Gider">{c.activity?.expense_count ?? 0}</Row>
          </div>
        </SectionCard>

        <SectionCard title="Fatura ve Firma Bilgileri">
          <div className="p-5" data-testid="admin-detail-billing-info">
            <Row label="Hesap Türü">
              {BILLING_TYPE_LABEL[c.billing?.billing_type] || "—"}
            </Row>
            {c.billing?.billing_type === "company" && (
              <Row label="Şirket Türü">
                {COMPANY_KIND_LABEL[c.billing?.company_kind] || "—"}
              </Row>
            )}
            {c.billing?.billing_type === "individual" && (
              <>
                <Row label="Ad">{c.billing?.first_name || "—"}</Row>
                <Row label="Soyad">{c.billing?.last_name || "—"}</Row>
                <Row label="T.C. Kimlik No">{c.billing?.national_id || "—"}</Row>
              </>
            )}
            {c.billing?.billing_type === "company" && (
              <>
                <Row label="Firma / Ticari Ünvan">{c.billing?.legal_name || "—"}</Row>
                <Row label="Yetkili Ad Soyad">
                  {c.billing?.authorized_person || "—"}
                </Row>
                {c.billing?.company_kind === "sahis_sirketi" ? (
                  <Row label="T.C. Kimlik No">{c.billing?.national_id || "—"}</Row>
                ) : (
                  <Row label="Vergi Numarası">{c.billing?.tax_number || "—"}</Row>
                )}
                <Row label="Vergi Dairesi">{c.billing?.tax_office || "—"}</Row>
                <Row label="Fatura Adresi">{c.billing?.billing_address || "—"}</Row>
                <Row label="İl">{c.billing?.billing_city || "—"}</Row>
                <Row label="İlçe">{c.billing?.billing_district || "—"}</Row>
              </>
            )}
          </div>
        </SectionCard>

        <RenewalSettingsCard company={c} onUpdated={setC} />

        <SectionCard title="Yönetim">
          <div className="p-5 space-y-4" data-testid="admin-detail-management">
            {isLocked && (
              <div
                className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800"
                data-testid="admin-mgmt-locked-notice"
              >
                Firma <b>{lifeState === "archived" ? "arşivde" : "silinmişlerde"}</b>. Durumunu değiştirmek için önce aşağıdaki <b>Geri Yükle</b> işlemini kullanın.
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs">Hesap Durumu</Label>
              <Select value={mgmtAccount} onValueChange={setMgmtAccount} disabled={isLocked}>
                <SelectTrigger className="h-10 rounded-lg" data-testid="admin-mgmt-account-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Aktif</SelectItem>
                  <SelectItem value="passive">Pasif</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Abonelik Tipi</Label>
              <Select value={mgmtSubscription} onValueChange={setMgmtSubscription} disabled={isLocked}>
                <SelectTrigger className="h-10 rounded-lg" data-testid="admin-mgmt-subscription-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="trial">Deneme</SelectItem>
                  <SelectItem value="paid">Ücretli</SelectItem>
                  <SelectItem value="internal_free">Dahili / Ücretsiz</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="pt-2 border-t border-slate-100" />

            <div className="space-y-1.5">
              <Label className="text-xs">Ödeme Dönemi</Label>
              <Select
                value={mgmtPeriod || "none"}
                onValueChange={(v) => setMgmtPeriod(v === "none" ? "" : v)}
                disabled={!paymentEditable || isLocked}
              >
                <SelectTrigger
                  className="h-10 rounded-lg"
                  data-testid="admin-mgmt-period-select"
                >
                  <SelectValue placeholder="Seçiniz" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Seçilmedi —</SelectItem>
                  <SelectItem value="monthly">Aylık</SelectItem>
                  <SelectItem value="yearly">Yıllık</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Ücret (TL)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                placeholder="Örn. 400"
                value={mgmtFee}
                onChange={(e) => setMgmtFee(e.target.value)}
                disabled={!paymentEditable || isLocked}
                className="h-10 rounded-lg"
                data-testid="admin-mgmt-fee-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Son Ödeme Tarihi</Label>
                <Input
                  type="date"
                  value={mgmtLastPayment}
                  onChange={(e) => setMgmtLastPayment(e.target.value)}
                  disabled={!paymentEditable || isLocked}
                  className="h-10 rounded-lg"
                  data-testid="admin-mgmt-last-payment-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Sonraki Ödeme Tarihi</Label>
                <Input
                  type="date"
                  value={mgmtNextPayment}
                  readOnly
                  tabIndex={-1}
                  disabled={!paymentEditable || isLocked}
                  className="h-10 rounded-lg bg-slate-50 cursor-not-allowed"
                  data-testid="admin-mgmt-next-payment-input"
                />
                <p className="text-[11px] text-slate-500">
                  Ödeme dönemi ve son ödeme tarihine göre otomatik hesaplanır.
                </p>
              </div>
            </div>

            <div className="pt-2 flex justify-end">
              <Button
                onClick={onSave}
                disabled={saving || isLocked}
                className="bg-slate-900 hover:bg-slate-800 rounded-lg"
                data-testid="admin-mgmt-save"
              >
                {saving ? "Kaydediliyor..." : "Kaydet"}
              </Button>
            </div>

            <div className="pt-4 border-t border-slate-100 space-y-3" data-testid="admin-lifecycle-actions">
              <div className="text-xs uppercase tracking-wider text-slate-500">Yaşam Döngüsü</div>
              <div className="flex flex-wrap gap-2">
                {canArchive && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-lg text-amber-700 border-amber-200 hover:bg-amber-50"
                    onClick={() => setLifecycleAction("archive")}
                    data-testid="admin-lifecycle-archive"
                  >
                    <Archive className="w-4 h-4 mr-1" /> Arşive Al
                  </Button>
                )}
                {canSoftDelete && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-lg text-rose-600 border-rose-200 hover:bg-rose-50"
                    onClick={() => setLifecycleAction("soft-delete")}
                    data-testid="admin-lifecycle-soft-delete"
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> Silinmişler&apos;e Taşı
                  </Button>
                )}
                {canRestore && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-lg"
                    onClick={() => setLifecycleAction("restore")}
                    data-testid="admin-lifecycle-restore"
                  >
                    <Undo2 className="w-4 h-4 mr-1" /> Geri Yükle
                  </Button>
                )}
                {canPermanent && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-lg text-rose-700 border-rose-200 hover:bg-rose-50"
                    onClick={() => setPermStep1Open(true)}
                    data-testid="admin-lifecycle-permanent"
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> Kalıcı Olarak Sil
                  </Button>
                )}
              </div>
              <p className="text-[11px] text-slate-500">
                Yaşam döngüsü: Aktif → Pasif → Arşiv → Silinmiş. Firma verileri kalıcı imha yapılmadıkça korunur; geri yükleme abonelik durumunu değiştirmez.
              </p>
            </div>

            <p className="text-xs text-slate-500 pt-2 border-t border-slate-100">
              Pasif firma normal kullanıcı girişinde engellenir. Dahili / Ücretsiz firma ödeme veya deneme kontrolüne tabi tutulmaz.
            </p>
          </div>
        </SectionCard>
      </div>

      {/* Lifecycle action confirmation (archive / soft-delete / restore) */}
      <AlertDialog
        open={!!lifecycleAction}
        onOpenChange={(v) => !v && setLifecycleAction(null)}
      >
        <AlertDialogContent data-testid="admin-lifecycle-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {lifecycleAction && lifecycleCopy[lifecycleAction]?.title}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {lifecycleAction && lifecycleCopy[lifecycleAction]?.body}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-lifecycle-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={runLifecycle}
              disabled={lifecycleBusy}
              className={
                lifecycleAction && lifecycleCopy[lifecycleAction]?.cls
              }
              data-testid="admin-lifecycle-ok"
            >
              {lifecycleBusy
                ? "İşleniyor…"
                : lifecycleAction && lifecycleCopy[lifecycleAction]?.ok}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Permanent delete — step 1 */}
      <AlertDialog
        open={permStep1Open}
        onOpenChange={setPermStep1Open}
      >
        <AlertDialogContent data-testid="admin-permanent-confirm-1">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="w-5 h-5" /> Kalıcı silme — 1. Onay
            </AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium">{c.name}</span> firması ve bağlı tüm veriler (müşteriler, tedarikçiler, işler, tahsilatlar, satın almalar, giderler, kasa hareketleri, kategoriler, kullanıcı) <b>geri dönüşü olmayacak şekilde</b> silinmek üzere.
              <br /><br />
              <b>Not:</b> Kalıcı silme şu an aktif değildir. PayTR ödeme audit verilerinin korunması gerektiğinden endpoint henüz devrede olmayabilir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-permanent-cancel-1">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setPermStep1Open(false); setPermTypedName(""); setPermStep2Open(true); }}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="admin-permanent-next"
            >
              Devam
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Permanent delete — step 2 typed confirmation */}
      <AlertDialog open={permStep2Open} onOpenChange={(v) => { setPermStep2Open(v); if (!v) setPermTypedName(""); }}>
        <AlertDialogContent data-testid="admin-permanent-confirm-2">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="w-5 h-5" /> Kalıcı silme — 2. Onay
            </AlertDialogTitle>
            <AlertDialogDescription>
              Onaylamak için firma adını aynen yazın:
              <br />
              <span className="font-semibold text-slate-900">{c.name}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="pt-2">
            <Input
              value={permTypedName}
              onChange={(e) => setPermTypedName(e.target.value)}
              placeholder="Firma adını yazın"
              className="h-10 rounded-lg"
              data-testid="admin-permanent-typed-name"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-permanent-cancel-2">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={runPermanent}
              disabled={lifecycleBusy || (permTypedName || "").trim() !== (c.name || "").trim() || !c.name}
              className="bg-rose-600 hover:bg-rose-700 disabled:opacity-40"
              data-testid="admin-permanent-confirm-ok"
            >
              {lifecycleBusy ? "İşleniyor…" : "Kalıcı Olarak Sil"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
